import { useRef, useState } from 'react';
import { switchRole } from '../api';
import { useSession } from './session-context';
import { useLanguage } from './language-context';
import { useLayer } from './layer-stack';
import { surfaceForRole, surfaceOrigin } from './surface-links';
import type { MessageKey } from '@bookingdei/i18n/message-key';

// "Continue as…" role picker (M32). One login can hold more than one role (customer +
// merchant + admin — DB-1); this lets an admin who also books switch to the customer
// app without a second sign-in. Rendered in the rail. Shows nothing for a single-role
// account, so it costs nothing for the common case. Duplicated per surface (CB-D3).
//
// On choose: switchRole re-issues the token pair scoped to the role (and rotates the
// shared bd_refresh cookie), then we send the user to that role's own surface, which
// comes up already in the switched context via its own cookie-backed refresh.

const ROLE_LABEL_KEY: Record<string, MessageKey> = {
  customer: 'user.role.customer',
  merchant: 'user.role.merchant',
  staff: 'user.role.staff',
  admin: 'user.role.admin',
};

export function AccountSwitcher() {
  const { user } = useSession();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // DC123: the menu is a popover layer — Esc closes it and focus returns to the trigger.
  const menuRef = useRef<HTMLDivElement>(null);
  useLayer(open, () => setOpen(false), { blocking: false });

  const roles = user?.roles ?? [];
  const active = user?.active_role;
  const others = roles.filter((r) => r !== active);
  // Single-role account (the common case): no picker at all.
  if (others.length === 0) return null;

  async function choose(role: (typeof roles)[number]) {
    setFailed(false);
    setBusy(role);
    try {
      await switchRole(role);
      // Land on the surface that role lives on. A null origin (localhost with no
      // override) falls back to a same-surface reload so /me re-fetches the context.
      const origin = surfaceOrigin(surfaceForRole(role));
      window.location.href = origin ? `${origin}/` : '/';
    } catch {
      setBusy(null);
      setFailed(true);
    }
  }

  return (
    <div className="acct-switch">
      <button
        type="button"
        className="acct-switch__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">⇄</span> {t('user.account.switch_heading')}
      </button>
      {open && (
        <div className="acct-switch__menu" role="menu" ref={menuRef} tabIndex={-1}>
          {others.map((role) => {
            const label = t(ROLE_LABEL_KEY[role] ?? 'user.role.customer');
            return (
              <button
                key={role}
                type="button"
                role="menuitem"
                className="acct-switch__opt"
                disabled={busy !== null}
                onClick={() => choose(role)}
              >
                {busy === role
                  ? t('user.account.switching')
                  : t('user.account.continue_as').replace('{role}', label)}
              </button>
            );
          })}
        </div>
      )}
      {open && failed && (
        <p className="acct-switch__error" role="alert">
          {t('user.account.switch_failed')}
        </p>
      )}
    </div>
  );
}
